import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAuth } from "./useAuth";
import { useChats } from "./useChats";
import { useFeatures } from "./useFeatures";
import api from "../services/api";
import { getDeviceId } from "../utils/deviceId";
import { isNonIdempotentRecording } from "../utils/recordingIdempotency";
import { isRecordingAutoRecoverEnabledFromStorage } from "../utils/recordingAutoRecoverFlag";
import { getActiveRecording } from "../utils/recordingHeartbeat";
import {
  claimRecoveryLock,
  releaseRecoveryLock,
  getGiveUp,
  bumpGiveUp,
  markGivenUp,
  RECOVERY_LOCK_TTL_MS,
  MAX_RECOVERY_GIVEUP,
} from "../utils/recordingRecoveryState";
import { isPermanentRecoveryError, RECORDING_SAVE_DEFERRED_OFFLINE } from "../utils/recordingSaveError";
import { isIdentityRefusal } from "../utils/recordingIdentityGuard";
import { isProvablyComplete } from "../utils/recordingCompleteness";
import { selectCrossDeviceCandidates, pendingRowGate, PENDING_GATE } from "../utils/recordingCrossDevice";
import { loadDismissedRecordingIds, persistDismissedRecordingId } from "../utils/recordingRecoveryDismissState";
import { markRecentlyRecovered, wasRecovered } from "../utils/recordingRecoveredNotice";
import { stitchSegmentsStreaming } from "../utils/audioStitch";

// Path B (cross-device recovery). Polls the server pending list for a WEB recording
// stranded on ANOTHER device, pulls its segment bytes, stitches, and re-uploads
// reusing the ORIGINAL recordingId (server M7 dedup → exactly one transcript).
// Gated entirely by ff_recordingAutoRecover. A PROVABLY-complete recording attaches
// silently — never a silent partial.
//
// AN INCOMPLETE RECORDING NOW GOES NOWHERE (2026-07-30). It is still computed into
// `nudgeCandidate` below, but the banner that rendered that state was removed, so
// nothing surfaces it and nobody can consent to a forced-partial save. The returned
// nudgeCandidate / recoverNudge / dismissNudge are retained because they are what a
// re-introduced prompt would bind to; today they are dead output. RecordingRecoveryBar
// still MOUNTS this hook (headless) — that mount is what keeps the silent path alive.
//
// This hook now owns the only pending-list poll; the bar's duplicate poll went with
// its rows.
const POLL_MS = 60000;
const SEGMENT_SECS = 10;
// A focus flip may trigger at most one sweep per this window. Well under POLL_MS so
// a genuine return-to-tab still refreshes promptly, but alt-tabbing can't drive the
// per-candidate loop at screen-refresh rates.
const VISIBILITY_POLL_MIN_GAP_MS = 15000;
// How many consecutive empty Get Segments reads retire a recording FOR THIS SESSION.
// Deliberately looser than MAX_RECOVERY_GIVEUP (a failed recovery is stronger
// evidence than an empty read) but finite — see the empty-segments branch in poll()
// for why an infinite budget is what produced the request storm, and why this is
// session-scoped rather than a persistent give-up. ~10 min of polling.
const EMPTY_GIVEUP_ROUNDS = 10;
// Ceiling on how many recordings the strike map tracks at once. A backlog larger
// than this evicts oldest-first; those ids simply get re-counted later, which is
// harmless because the per-sweep candidate cap already bounds the request rate.
const EMPTY_ROUNDS_MAX_TRACKED = 500;

// Pull every listed segment, stitch, upload. recordingGetSegmentBytes does NOT
// swallow errors, so any failed pull throws here → HARD ABORT (never upload a
// partial — a file swept between the list snapshot and the pull would otherwise
// silently truncate the transcript). Returns { allDecodeFailed } so the caller
// can nudge-to-origin on a Chrome<->Safari codec mismatch instead of saving, and
// { partialDecode } when SOME segments failed to decode — the caller must then
// downgrade to the nudge, never silently attach the gap-truncated result.
//
// MEMORY: pulls ONE segment at a time. This previously held every segment's base64
// in memory at once (a `byIndex` map built before stitching), which the original
// comment accepted as "the price of the hard-abort guarantee" — because the
// stitcher's lazy fetchSegmentAudio SWALLOWS a failed read (failedCount++/continue,
// and a falsy return is read as a benign empty segment), so streaming naively would
// silently truncate a transcript.
//
// That price stopped being acceptable: a recording is 100-360 segments at ~10s
// each, base64 inflates ~33%, and peak memory scaled with the whole recording on a
// path that can run for several recordings in one sweep. It is a prime suspect for
// the accompanying server/browser memory pressure.
//
// This is the alternative the old comment itself prescribed — "pull one at a time
// WITH a throw on any failure, never revert to lazy fetch": the fetch happens inside
// fetchSegmentAudio, but any failure is captured in `pullError` and RETHROWN before
// the result can be read, so a failed pull can never degrade into a silent gap.
// Peak memory is now one segment plus the encoder's accumulating MP3.
// forcedPartial (from the caller's `fromNudge`): true when recovering an INCOMPLETE
// recording via the "Recover anyway" nudge. Threaded to parseRecording -> api.transcript
// so the backend saves a supersed-able PARTIAL and keeps the segment backstop, letting
// the origin device's later COMPLETE save win. The silent-attach poll path (complete
// recordings) omits it → default false → unchanged.
export async function pullStitchUpload(recording, segments, parseRecording, forcedPartial = false) {
  // Dedupe by index (a duplicate manifest row can list one index twice) before
  // ordering — a doubled key would stitch the same 10s of audio twice.
  const seenIdx = new Set();
  const ordered = [...(segments || [])]
    .sort((a, b) => a.index - b.index)
    .filter((s) => (seenIdx.has(s.index) ? false : (seenIdx.add(s.index), true)));
  if (!ordered.length) throw new Error("no segments to pull");
  const segByIndex = new Map(ordered.map((s) => [s.index, s]));
  // Container hint drives only the trailing-fragment tail bound, never decoding, so
  // taking it from the LISTED mime is equivalent to the old "mime of the last fetched
  // segment" without needing every segment in hand first.
  const containerMime = ordered[ordered.length - 1].mimeType || "audio/webm";

  // Captured rather than thrown: throwing from inside fetchSegmentAudio would be
  // caught by the stitcher's own try/catch and merely counted, which is exactly the
  // silent truncation this must prevent. Rethrown below before any result is used.
  let pullError = null;
  const fetchSegmentAudio = async (idx) => {
    // Once doomed, stop pulling — no reason to move more PHI over the wire for a
    // result we are about to discard.
    if (pullError) return undefined;
    const seg = segByIndex.get(idx);
    if (!seg) {
      pullError = new Error("no manifest entry for segment " + idx);
      return undefined;
    }
    try {
      const r = await api.recordingGetSegmentBytes(recording.recordingId, seg.fileName);
      if (!r || !r.file) throw new Error("missing bytes for segment " + idx);
      return "data:" + (r.mimeType || seg.mimeType || "audio/webm") + ";base64," + r.file;
    } catch (e) {
      pullError = e instanceof Error ? e : new Error(String(e));
      return undefined;
    }
  };

  const orderedKeys = ordered.map((s) => s.index);
  const maxIndex = orderedKeys[orderedKeys.length - 1];
  const { mp3DataUrl, allDecodeFailed, failedCount } = await stitchSegmentsStreaming({
    orderedKeys,
    fetchSegmentAudio,
    durationSecsEstimate: (maxIndex + 1) * SEGMENT_SECS,
    containerMime,
  });
  // HARD ABORT, before anything can read mp3DataUrl. A retrieval failure means a file
  // was swept between the list snapshot and the pull; the stitcher will happily have
  // produced audio with that segment missing, and uploading it would silently
  // truncate the transcript.
  if (pullError) throw pullError;
  if (allDecodeFailed) return { allDecodeFailed: true };
  // SILENT-ATTACH SAFETY (decode leg): the completeness gate proved every segment
  // is PRESENT on the server, but the stitcher skips a segment whose bytes won't
  // DECODE (failedCount) and still returns audio. Attaching that silently would
  // save a transcript with a hidden hole — return partialDecode so the caller
  // downgrades to the explicit "Recover anyway?" nudge instead. A nudge-initiated
  // recover (forcedPartial) already carries user consent for gaps, so it proceeds.
  if (!forcedPartial && failedCount > 0) return { partialDecode: true };
  if (!mp3DataUrl) throw new Error("cross-device stitch produced no audio");
  const saveOutcome = await parseRecording(
    mp3DataUrl,
    recording.chatId,
    recording.phase,
    undefined,
    null,
    recording.recordingId,
    undefined,
    recording.startedAt,
    forcedPartial,
    // Encounter ids are recycled: re-check the target before attaching. A consented
    // (nudge) recover is an explicit user action, so the auto-drain age rule is off.
    { deferred: true, sourceChatId: recording.chatId, retry: !!forcedPartial }
  );
  // Refused by the identity guard: this audio belongs to some other encounter.
  if (isIdentityRefusal(saveOutcome)) return { refusedIdentity: saveOutcome };
  // A refusal, not a save: parseRecording resolves with the sentinel when the
  // webview believes it is offline (it never throws for this). Reporting success
  // here made attemptRecover mark the recording durably RECOVERED — and dismiss
  // the consent nudge forever — with no transcript attached. Surface it as its
  // own outcome; the caller leaves everything unmarked so the poll re-offers.
  if (saveOutcome === RECORDING_SAVE_DEFERRED_OFFLINE) return { deferredOffline: true };
  return { allDecodeFailed: false };
}

export function useCrossDeviceRecovery() {
  const { t } = useTranslation();
  const { auth, isServerAvailable, isDemoRef } = useAuth();
  const { parseRecording } = useChats();
  const { hasFeature } = useFeatures();
  // Dictation-only gate (Change 2): non-encounters orgs (peptides/DME/sales-only)
  // never poll Recording - Pending List for a recording they can't have made.
  const recordingFeatureEnabled = hasFeature("encounters");
  // Ids currently being recovered, as STATE (not a bare ref) so every membership
  // change re-renders — the bar keys the Recover button's disabled/"Recovering…"
  // presentation off this per-id, and a same-boolean setState would bail out of
  // the re-render.
  const [recoveringIds, setRecoveringIds] = useState([]);
  const [nudgeCandidate, setNudgeCandidate] = useState(null);
  const [recoveredTick, setRecoveredTick] = useState(0);
  const inFlight = useRef(new Set());
  const dismissedRef = useRef(new Set());
  // Recordings we've already found undecodable here (Chrome<->Safari container
  // mismatch). Session-scoped: the poll must NOT re-pull all their bytes (full PHI)
  // every 60s just to fail the same decode — surface the open-origin-device nudge
  // once and stop re-pulling. Resets on reload (one retry per session is fine).
  const codecMismatchRef = useRef(new Set());
  // Same PHI-re-pull hazard, decode-gap flavor: a provably-complete recording with
  // one undecodable segment downgrades to the nudge, but the failure is
  // deterministic — without this memo the poll would re-pull every byte each 60s
  // just to hit the same gate while the nudge sits unanswered. A "Recover anyway"
  // click still works (forcedPartial bypasses the gate).
  const partialDecodeRef = useRef(new Set());
  // Consecutive polls in which Get Segments returned nothing for a recording, keyed
  // by recordingId. Feeds the EMPTY_GIVEUP_ROUNDS budget that retires a recording
  // whose segments can never arrive. Session-scoped and NOT persisted — see the
  // empty-segments branch in poll() for why a durable give-up is the wrong tool here.
  const emptyRoundsRef = useRef(new Map());
  // Serializes poll runs (see guardedPoll) and rate-limits the visibility trigger.
  const pollingRef = useRef(false);
  const lastVisiblePollRef = useRef(0);
  const timerRef = useRef();
  // isServerAvailable, not isOnline: every poll run calls
  // api.recordingPendingList(). During a server outage those are doomed, and a
  // failing poll can burn finalless-recording give-up strikes against a
  // recording that was never actually unrecoverable.
  const serverAvailableRef = useRef(isServerAvailable);
  useEffect(() => {
    serverAvailableRef.current = isServerAvailable;
  }, [isServerAvailable]);

  // ON by default (global) — only an explicit ff_recordingAutoRecover="0" disables it.
  const enabled = isRecordingAutoRecoverEnabledFromStorage();
  // Native webview re-encode OOMs; demo must never pull real PHI. Skip both.
  const skipEnvironment = (typeof window !== "undefined" && !!window.ReactNativeWebView) || !!(isDemoRef && isDemoRef.current);

  // Session run token. The poll effect bumps it on every setup AND teardown
  // (logout, viewer switch, demo entry, flag flip); an attemptRecover captures the
  // token at entry and refuses to upload or write a "Recovered" notice if it no
  // longer matches — so a recovery started under viewer A can never finish into
  // viewer B's freshly-keyed stores. (A plain boolean reset on re-setup would
  // re-open exactly that window.)
  const runTokenRef = useRef(0);

  // Recover ONE recording: pull -> stitch -> upload (reuse id). Single-flight +
  // cross-tab lock; classifies permanent (signed) vs transient on failure.
  const attemptRecover = useCallback(
    async (recording, { fromNudge, segments, segmentsExpected } = {}) => {
      if (!recording || !recording.recordingId) return;
      if (inFlight.current.has(recording.recordingId)) return; // single-flight (sync, pre-await)
      inFlight.current.add(recording.recordingId);
      const lockHeld = claimRecoveryLock(recording.recordingId, RECOVERY_LOCK_TTL_MS);
      if (!lockHeld) {
        inFlight.current.delete(recording.recordingId);
        return; // another tab owns the recovery
      }
      const runToken = runTokenRef.current;
      setRecoveringIds(Array.from(inFlight.current));
      try {
        let segs = segments;
        let expected = segmentsExpected;
        if (!segs) {
          const got = await api.recordingGetSegments(recording.recordingId);
          segs = got.segments;
          expected = got.segmentsExpected;
        }
        // SILENT-ATTACH SAFETY: the poll gated completeness on presentIndices, but the
        // bytes we actually upload come from `segments`. Re-prove completeness on the
        // SAME data we're about to send — if `segments` is not contiguous 0..expected-1
        // (the two server fields disagreed), do NOT silently attach; downgrade to a
        // nudge. The nudge path (fromNudge) is intentionally partial, so it skips this.
        if (!fromNudge && !isProvablyComplete({ segmentsExpected: expected, presentIndices: (segs || []).map((s) => s.index) })) {
          setNudgeCandidate((c) => c || recording);
          return;
        }
        if (runTokenRef.current !== runToken) return; // session/viewer changed while fetching the index
        // fromNudge === true is the forced-partial recover (an INCOMPLETE recording the
        // user accepted); it flows through as forcedPartial so the backend saves a
        // supersed-able partial. The silent-attach poll path leaves it false.
        const { allDecodeFailed, partialDecode, deferredOffline, refusedIdentity } = await pullStitchUpload(
          recording,
          segs || [],
          parseRecording,
          !!fromNudge
        );
        if (allDecodeFailed) {
          // Codec mismatch — can't decode the other device's container here. Don't
          // save; nudge the user to open the origin device, and REMEMBER it so the
          // poll stops re-pulling all the bytes every 60s (a PHI re-pull storm) just
          // to fail the same decode. Don't steal the slot from a DIFFERENT nudge the
          // user may be about to act on — the poll re-offers this one when it frees.
          codecMismatchRef.current.add(recording.recordingId);
          setNudgeCandidate((c) => (c && c.recordingId !== recording.recordingId ? c : { ...recording, codecMismatch: true }));
          return;
        }
        if (partialDecode) {
          // Some segment bytes exist but won't decode — a silent attach would hide a
          // gap. Surface the explicit consent nudge instead, and REMEMBER the id so
          // the poll doesn't re-pull all the bytes every 60s to re-fail the same gate.
          partialDecodeRef.current.add(recording.recordingId);
          setNudgeCandidate((c) => c || recording);
          return;
        }
        if (refusedIdentity) {
          // Its encounter id now names a different encounter. Nothing was attached;
          // stop re-pulling it for good (parseRecording already reported the park).
          markGivenUp(recording.recordingId);
          setNudgeCandidate((c) => (c && c.recordingId === recording.recordingId ? null : c));
          return;
        }
        if (deferredOffline) {
          // The webview decided it was offline mid-pull (the poll's isOnline gate
          // passed BEFORE the multi-minute pull). Nothing was uploaded, so nothing
          // may be marked: no recovered mark, no nudge dismissal, no give-up strike
          // (the audio and server rows are fine — this is connectivity, not
          // corruption). parseRecording already showed its "will be saved when
          // online" toast; the poll's own gate holds further pulls while offline
          // and re-offers this recording when the webview is back.
          return;
        }
        if (runTokenRef.current !== runToken) return; // logged out mid-upload — don't write into the next viewer's store
        // Silent on success — this mark only suppresses re-recovery/re-nags; no UI
        // announces it (the transcript on the encounter is the record).
        markRecentlyRecovered(recording.recordingId, recording.chatId, recording.startedAt);
        if (fromNudge) {
          // The user consented to a partial save — leave the warning up until they
          // close it (a 3s toast is not consent; spec §12.6), and stop re-offering
          // the nudge for a recording they already accepted: the server row stays
          // pending ON PURPOSE (so the origin device's complete save can supersede),
          // which would otherwise re-arm this same prompt every poll for days.
          toast.warning(t("hooks.useIndexDb.recordingPartiallyProcessed"), { autoClose: false });
          persistDismissedRecordingId(recording.recordingId);
          dismissedRef.current.add(recording.recordingId);
        }
        setNudgeCandidate((c) => (c && c.recordingId === recording.recordingId ? null : c));
        setRecoveredTick((n) => n + 1);
      } catch (err) {
        if (err?.serverUnavailable) {
          // Server unreachable mid-recovery. The audio still exists server-side (the
          // pull precedes the upload gate) and nothing was saved, so this is neither a
          // failed recovery nor a reason to burn a bounded give-up strike: no strike,
          // no permanent dismissal, and no "recover failed" toast (parseRecording
          // already told the clinician it will be processed when back online).
          return;
        }
        if (isPermanentRecoveryError(err)) {
          // Encounter signed/archived — can NEVER attach. Durably stop re-pulling +
          // tell the clinician once; suppress the danger toast for the background poll.
          markGivenUp(recording.recordingId);
          toast.info(t("hooks.useIndexDb.recoveredCouldNotAttachSigned"));
        } else if (fromNudge) {
          // A user-initiated "Recover" click that fails must SAY so — silence reads as
          // a dead button. It does NOT consume a give-up strike: the user sees the
          // failure and decides (retry or dismiss); burning the bounded strikes on
          // manual clicks could permanently kill recovery after three flaky-network
          // taps. Log the cause for diagnosis (never PHI — message/status only).
          console.error(
            "[cross-device recovery] recover failed —",
            (err && err.message) || err,
            err && err.response && err.response.status ? "(HTTP " + err.response.status + ")" : ""
          );
          toast.error(t("hooks.useIndexDb.recoverFailed"));
        } else {
          // Transient OR a HARD-ABORT pull failure on the background poll — bound the
          // retries so a swept / permanently-broken recording can't re-pull forever.
          // Never saved a partial.
          bumpGiveUp(recording.recordingId);
        }
      } finally {
        releaseRecoveryLock(recording.recordingId);
        inFlight.current.delete(recording.recordingId);
        setRecoveringIds(Array.from(inFlight.current));
      }
    },
    [parseRecording, t]
  );
  // The poll effect deliberately has narrow deps (one interval per session), so it
  // must call the FRESHEST attemptRecover through a ref — the closure it captured at
  // mount holds that render's parseRecording, whose stale server-availability/language/chats
  // would make a recovery silently no-op the upload yet still mark "Recovered".
  const attemptRecoverRef = useRef(attemptRecover);
  useEffect(() => {
    attemptRecoverRef.current = attemptRecover;
  }, [attemptRecover]);

  // The nudge "Recover anyway?" action — recover an INCOMPLETE recording on demand
  // (the user accepts the partial-loss risk; we warn after the save).
  const recoverNudge = useCallback(
    (recordingId) => {
      // Read the candidate directly (deps include nudgeCandidate), not inside a
      // setState updater — an updater may run twice under StrictMode/concurrent and
      // would fire attemptRecover twice (single-flight saves us, but don't rely on it).
      if (nudgeCandidate && nudgeCandidate.recordingId === recordingId && !nudgeCandidate.codecMismatch) {
        attemptRecover(nudgeCandidate, { fromNudge: true });
      }
    },
    [nudgeCandidate, attemptRecover]
  );

  // Dismiss the nudge without recovering — persist it so it stays gone across polls and
  // reloads (mirrors the legacy bar's dismiss). Without this the "Recover it?" prompt
  // had no off-switch and could get stuck on screen.
  const dismissNudge = useCallback((recordingId) => {
    persistDismissedRecordingId(recordingId);
    dismissedRef.current.add(recordingId);
    setNudgeCandidate((c) => (c && c.recordingId === recordingId ? null : c));
  }, []);

  useEffect(() => {
    if (!enabled || skipEnvironment || !auth?.user?.id || !recordingFeatureEnabled) return undefined;
    let cancelled = false;
    runTokenRef.current += 1; // invalidate any recovery still in flight from a prior session/viewer
    const myDevice = getDeviceId();

    const poll = async () => {
      if (!serverAvailableRef.current || (typeof document !== "undefined" && document.visibilityState !== "visible")) return;
      // Re-read dismissals every poll: the legacy bar, another tab, or this hook's
      // own nudge-dismiss all write the same store, and a once-at-mount snapshot
      // let a recording dismissed on one surface re-surface on the other.
      dismissedRef.current = loadDismissedRecordingIds();
      const pending = await api.recordingPendingList();
      if (cancelled) return;
      const candidates = selectCrossDeviceCandidates(pending, {
        myDevice,
        myUserId: auth?.user?.id,
        now: Date.now(),
        isDismissed: (id) => dismissedRef.current.has(id),
        activeRecordingId: getActiveRecording(),
        giveUpOf: getGiveUp,
        maxGiveUp: MAX_RECOVERY_GIVEUP,
        isNonIdempotent: isNonIdempotentRecording,
        inFlight: inFlight.current,
        wasRecovered,
        isEmptySkipped: (id) => (emptyRoundsRef.current.get(id) || 0) >= EMPTY_GIVEUP_ROUNDS,
      });
      // Drop a stale nudge: if the recording we were nudging is no longer a live
      // candidate (recovered on another device, swept, marked done, or given up), clear
      // it so the "Recover it?" prompt doesn't linger after there's nothing to recover.
      // An in-flight recovery is NOT stale — candidates excludes in-flight ids, so
      // without the exemption the user's own "Recover" click would erase the prompt
      // mid-recovery and the button would appear to do nothing.
      setNudgeCandidate((c) =>
        c && !inFlight.current.has(c.recordingId) && !candidates.some((r) => r.recordingId === c.recordingId) ? null : c
      );
      // The server holds no fetchable bytes for this recording right now. Clear any
      // dead nudge and spend a strike from the session budget — see the long note at
      // the call site below for why the budget exists and why it is not persisted.
      const noteEmptyRound = (recordingId) => {
        setNudgeCandidate((c) => (c && c.recordingId === recordingId ? null : c));
        const strikes = (emptyRoundsRef.current.get(recordingId) || 0) + 1;
        // Stop counting at the cap so the Map can't grow a hot entry unbounded, and
        // evict oldest-first if a huge backlog would otherwise retain every id.
        if (strikes <= EMPTY_GIVEUP_ROUNDS) {
          if (emptyRoundsRef.current.size >= EMPTY_ROUNDS_MAX_TRACKED) {
            const oldest = emptyRoundsRef.current.keys().next();
            if (!oldest.done) emptyRoundsRef.current.delete(oldest.value);
          }
          emptyRoundsRef.current.set(recordingId, strikes);
        }
      };

      for (const r of candidates) {
        if (cancelled) return;

        // FAST PATH. The pending list already carries the union facts the gate needs
        // (presentIndicesAll / segmentsExpectedAll, unioned server-side across
        // duplicate manifest rows), so a recording that is NOT provably complete can
        // be resolved without spending a Recording - Get Segments call on it. That is
        // the entire N+1: the backlog is dominated by recordings that fail this gate
        // on every poll, forever.
        //
        // Only a provably-complete recording still needs the call, because only the
        // recovery pull needs segment fileNames — so the SILENT-ATTACH re-proof in
        // attemptRecover is untouched, and nothing attaches on the strength of a
        // pending-list field.
        //
        // UNKNOWN falls through to the call. That covers a backend that does not send
        // the fields (this ships before the BE deploy) and any payload the normalizer
        // refused to trust — including a mangled nested array, which has never crossed
        // /web/run-process before. Guessing here would be the dangerous direction.
        const gate = pendingRowGate(r, isProvablyComplete);
        if (gate === PENDING_GATE.EMPTY) {
          noteEmptyRound(r.recordingId);
          continue;
        }
        if (gate === PENDING_GATE.PARTIAL) {
          // Incomplete but the server does hold bytes -> offer the nudge, never a
          // silent partial. Real bytes exist, so any empty-read strikes are stale.
          emptyRoundsRef.current.delete(r.recordingId);
          setNudgeCandidate((c) => c || r);
          continue;
        }

        const { status, presentIndices, segments, segmentsExpected } = await api.recordingGetSegments(r.recordingId);
        if (cancelled) return;
        if (isProvablyComplete({ segmentsExpected, presentIndices })) {
          if (codecMismatchRef.current.has(r.recordingId)) {
            // Already proven undecodable on this device — keep the open-origin-device
            // nudge up but DON'T re-pull the bytes again. (Kept INSIDE the
            // provably-complete branch on purpose: hoisting it above the segment call
            // to save a request would keep nudging a recording whose segments have
            // since been swept, and these memos hold at most a handful of ids.)
            setNudgeCandidate((c) => c || { ...r, codecMismatch: true });
          } else if (partialDecodeRef.current.has(r.recordingId)) {
            // Already proven gap-truncated here — keep the consent nudge alive but
            // DON'T re-pull the bytes to re-fail the same decode (PHI re-pull storm).
            setNudgeCandidate((c) => c || r);
          } else {
            // Pass segmentsExpected so attemptRecover re-proves completeness on `segments`
            // (the upload data) before silently attaching — see SILENT-ATTACH SAFETY.
            // Through the ref so a session-long interval never runs a stale closure.
            await attemptRecoverRef.current(r, { segments, segmentsExpected });
          }
        } else if (!Array.isArray(segments) || segments.length === 0) {
          // Pending but NO segments on the server to pull right now (uploads still in
          // flight/failed, already swept, or a transient Get Segments error). Nothing to
          // recover this round, so don't show a dead "Recover it?" nudge — and clear any
          // stale one for this recording so the single nudge slot frees up.
          //
          // This branch USED to return without consuming a strike ("a later poll will
          // nudge if segments do appear"), which made it unbounded: a recording whose
          // segments can never appear — the pre-BE-#228 case, where the deployed
          // Store Segment never wrote to the archive — was re-listed every 60s per tab
          // forever. That is the request storm.
          //
          // Strikes are SESSION-SCOPED on purpose. The obvious implementation, calling
          // markGivenUp at the cap, is wrong here: that write is permanent, and its
          // only reversal (clearGiveUp) is reachable solely from useIndexDb's
          // retryRecording, which reads the LOCAL IndexedDB row. A cross-device
          // recording has no local row on this device by definition — so a permanent
          // give-up would disable Path B for that recording on this browser forever,
          // with no UI path back, on nothing stronger than a transient empty read.
          // A reload re-arms instead, which is the right blast radius: the storm is
          // bounded within a session (the only place it hurts the server) and the
          // user's own refresh is the retry gesture.
          //
          // Only spend a strike when the SERVER ACTUALLY ANSWERED. api.recordingGetSegments
          // returns this same empty shape on any transport failure (status "error"), and
          // that now includes a 429 from the rate limit the backend puts on this very
          // process — so counting those would let throttling retire recordings that are
          // perfectly recoverable, which is precisely backwards. A definitive "rejected"
          // (not found / invalid id) DOES count: the server looked and there is nothing.
          if (status !== "error") noteEmptyRound(r.recordingId);
        } else {
          // Incomplete but HAS segments -> surface the first one as a nudge; never a
          // silent partial. Server bytes exist, so clear any empty-read strikes.
          emptyRoundsRef.current.delete(r.recordingId);
          setNudgeCandidate((c) => c || r);
        }
      }
    };

    // setInterval does not await poll, and a poll that reaches attemptRecover pulls
    // every segment's bytes serially — routinely longer than POLL_MS. Without this
    // guard the next tick starts a SECOND concurrent sweep over the same candidate
    // list, and slow recoveries beget more concurrent sweeps. Guard the whole async
    // body, not just the entry, or the await points re-open the window.
    const guardedPoll = async () => {
      if (pollingRef.current) return;
      pollingRef.current = true;
      try {
        await poll();
      } finally {
        pollingRef.current = false;
      }
    };

    guardedPoll();
    timerRef.current = setInterval(guardedPoll, POLL_MS);
    // Every focus flip used to run a full sweep immediately — alt-tabbing drove the
    // per-candidate loop at an unbounded rate. The in-flight guard alone doesn't fix
    // that (a fast sweep finishes between flips), so rate-limit the trigger too.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastVisiblePollRef.current < VISIBILITY_POLL_MIN_GAP_MS) return;
      lastVisiblePollRef.current = now;
      guardedPoll();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      runTokenRef.current += 1;
      clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // poll reads isServerAvailable via the ref; interval created once per session/flag change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user?.id, enabled, skipEnvironment, recordingFeatureEnabled]);

  // recoveringIds lets the bar key per-recording presentation (disable/relabel the
  // Recover button for THE recording being recovered, suppress the contradictory
  // legacy row) — a recovery of one recording must not disable another's button.
  return {
    recoveringIds,
    nudgeCandidate,
    recoverNudge,
    dismissNudge,
    recoveredTick,
  };
}
